import { useState, useEffect } from 'react';
import { graphql, useFragment } from 'react-relay';
import { nanoid } from 'nanoid';
import Icon from '@/core/icon.js';
import { useTranslation } from '@/helpers/translate';
import { useSpacePublicZustandStore } from '@/helpers/spacepublicprovider';

const queryCalendar = graphql`
  fragment calendarSpaceCalendar_calendar on CalendarByDay @relay(plural: true) {
    day
    checkin
    checkout
    checkinMorning
    checkoutMorning
    checkinAfternoon
    checkoutAfternoon
    checkinNight
    checkoutNight
  }
`;

function Calendar({ calendar, dataByCategory, extrahour }) {
  const { t } = useTranslation();
  const [reactKey] = useState(nanoid());

  if (!calendar) return null;

  const schedule = calendar.find((c) => c.checkin);
  const {
    checkin, checkout, checkinMorning, checkoutMorning,
    checkinAfternoon, checkoutAfternoon, checkinNight, checkoutNight,
  } = schedule || {};

  const formatHour = (hour) => hour?.replace(/:00$/, '');

  // Créer un tableau avec tous les jours de la semaine
  const allDays = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];

  return (
    <section className="flex-col mt-12 md:mt-12 text-purple-5/60 md:flex-row">
      <h2 className="w-full pt-6 pb-2 text-xl font-medium text-black border-t border-white-dark2 md:pt-12 lg:pb-5">{t('space:disponibility')}</h2>
      <div className="grid grid-cols-4 gap-2 md:grid-cols-7">
        {allDays.map((day) => {
          const isAvailable = calendar.some((c) => c.day === day);
          return (
            <span
              key={`calendar${reactKey}${day}`}
              className={`rounded-lg text-sm text-center ${isAvailable ? 'bg-purple/10 text-purple' : 'bg-gray-100 text-gray-400'} py-1`}
            >
              {t(`date:day_3.${day}`)}
            </span>
          );
        })}
      </div>
      <div className="flex pt-5 pb-7 gap-x-14">
        <div className="flex items-center gap-x-2">
          <div className="flex items-center">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="12" cy="12" r="11" fill="white" stroke="#857F91" />
              <circle cx="12" cy="12" r="8" fill="#865FFF" />
            </svg>
          </div>
          <div className="flex flex-col">
            <span className="text-sm">{t('btn:period.day')}</span>
            {' '}
            <span className="font-medium text-[15px] text-black">{t('space:hourrange', { checkin: formatHour(checkin), checkout: formatHour(checkout) })}</span>
          </div>
        </div>
        {!(dataByCategory.allow_halfday && checkinMorning && checkoutMorning) || (
        <div className="flex items-center gap-x-3">
          <div className="flex items-center">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="12" cy="12" r="11" fill="white" stroke="#857F91" />
              <path d="M20 12C17 12 16.4183 12 12 12C7.58172 12 7.5 12 4 12C4 7.58172 7.58172 4 12 4C16.4183 4 20 7.58172 20 12Z" fill="#865FFF" />
            </svg>
          </div>
          <div className="flex flex-col">
            <span className="text-sm">{t('btn:period.morning')}</span>
            {' '}
            <span className="font-medium text-[15px] text-black">{t('space:hourrange', { checkin: formatHour(checkinMorning), checkout: formatHour(checkoutMorning) })}</span>
          </div>
        </div>
        )}
        {!(dataByCategory.allow_halfday && checkinAfternoon && checkoutAfternoon) || (
        <div className="flex items-center gap-x-2">
          <div className="flex items-center">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="12" cy="12" r="11" fill="white" stroke="#857F91" />
              <path d="M4 12C7 12 7.58172 12 12 12C16.4183 12 16.5 12 20 12C20 16.4183 16.4183 20 12 20C7.58172 20 4 16.4183 4 12Z" fill="#865FFF" />
            </svg>
          </div>
          <div className="flex flex-col">
            <span className="text-sm">{t('btn:period.afternoon')}</span>
            {' '}
            <span className="font-medium text-[15px] text-black">{t('space:hourrange', { checkin: formatHour(checkinAfternoon), checkout: formatHour(checkoutAfternoon) })}</span>
          </div>
        </div>
        )}
        {!(dataByCategory.allow_night && checkinNight && checkoutNight) || (
        <div className="flex items-center gap-x-2">
          <div className="flex items-center">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="12" cy="12" r="11" fill="white" stroke="#857F91" />
              <path d="M4 12C8 15.5 16 15.5 20 12C20 16.4183 16.4183 20 12 20C7.58172 20 4 16.4183 4 12Z" fill="#865FFF" />
            </svg>
          </div>
          <div className="flex flex-col">
            <span className="text-sm">{t('btn:period.night')}</span>
          {' '}
          <span className="font-medium text-[15px] text-black">{t('space:hourrange', { checkin: formatHour(checkinNight), checkout: formatHour(checkoutNight) })}</span>
          </div>
        </div>
        )}
      </div>
      {!extrahour || (
      <div className="flex flex-row items-center">
        <div className="shrink">
          <svg className="mr-3 size-5" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path fillRule="evenodd" clipRule="evenodd" d="M16.2073 18.1782C15.0495 18.823 13.3619 18.823 10.0007 18.823C6.6367 18.823 4.94811 18.823 3.79415 18.1782C2.9668 17.718 2.2844 17.0356 1.82036 16.2054C1.17655 15.0467 1.17655 13.36 1.17655 9.99882C1.17655 6.63388 1.17655 4.94529 1.8213 3.79133C2.28157 2.96397 2.96397 2.28157 3.79415 1.81848C4.95188 1.17467 6.63859 1.17467 10.0007 1.17467C13.3647 1.17467 15.0533 1.17467 16.2073 1.81942C17.0337 2.27875 17.717 2.96115 18.181 3.79133C18.8249 4.95 18.8249 6.6367 18.8249 9.99882C18.8249 13.3628 18.8249 15.0514 18.181 16.2044C17.7198 17.0327 17.0374 17.7151 16.2073 18.1782M19.1995 16.7513C20.0005 15.315 20.0005 13.5388 20.0005 9.99976C20.0005 6.46069 20.0005 4.68457 19.1995 3.24823C18.6272 2.21945 17.781 1.37327 16.7522 0.800056C15.3159 0 13.5398 0 9.99976 0C6.46163 0 4.68457 0 3.24823 0.800056C2.22039 1.37233 1.37327 2.21851 0.800998 3.24729C0 4.68457 0 6.46069 0 9.99976C0 13.5388 0 15.315 0.800998 16.7513C1.37327 17.7801 2.21945 18.6263 3.24823 19.1985C4.68457 20.0005 6.46163 20.0005 9.99976 20.0005C13.5407 20.0005 15.3187 20.0005 16.7513 19.1995C17.781 18.6263 18.6272 17.7801 19.1995 16.7513" fill="#857F91" />
            <path fillRule="evenodd" clipRule="evenodd" d="M13.6234 12.5425C13.7354 12.2037 13.55 11.8338 13.2112 11.7189L10.7677 10.9038C10.0985 10.6826 9.64856 10.0595 9.64856 9.35358V5.99052C9.64856 5.63191 9.35772 5.34106 8.9991 5.34106C8.64143 5.34106 8.35059 5.63191 8.35059 5.99052V9.35358C8.35059 10.6167 9.15723 11.7359 10.3573 12.1378L12.7998 12.9538C13.1095 13.0555 13.5114 12.8767 13.6234 12.5425" fill="#857F91" />
          </svg>
        </div>
        <div className="flex flex-col text-balance">
          <span className="font-medium text-[15px] text-purple-5 leading-4">Flexible</span>
          <span className="text-sm text-purple-5/50">{t('space:extra_hour_ride')}</span>
        </div>
      </div>
      )}
    </section>
  );
}

export { queryCalendar };

function FragmentComponent({ calendar: calendarData, ...props }) {
  const calendar = useFragment(queryCalendar, calendarData || null);
  return <Calendar {...{ calendar }} {...props} />;
}

function ComponentRoot(props) {
  const lazycontentdataloaded = useSpacePublicZustandStore((state) => state.lazycontentdataloaded);
  const [data, setData] = useState(null);

  useEffect(() => {
    if (lazycontentdataloaded) {
      const { lazyCalendar } = useSpacePublicZustandStore.getState();
      setData({
        calendar: lazyCalendar,
      });
    }
  }, [lazycontentdataloaded]);

  if (!data) return null;
  return <FragmentComponent {...data} {...props} />;
}

export default ComponentRoot;
